import { useState, useEffect, useCallback } from 'react';

/**
 * Hook personalizzato per gestire l'inversione dei lati in un match
 * @param {string} matchId - ID del match
 * @returns {Object} - Oggetto con stato inversione, funzioni e utility
 */
export function useInversioneLati(matchId) {
  // Log per debug iniziale
  console.log(`[useInversioneLati] Hook inizializzato con matchId: ${matchId} (tipo: ${typeof matchId})`);

  // Stato per inversione lati
  const [invertiLati, setInvertiLati] = useState(false);

  // Funzione per caricare le preferenze di inversione da localStorage
  const loadInversionePreferences = useCallback(() => {
    if (matchId === undefined || matchId === null) {
      console.log(`[useInversioneLati] matchId non valido, skip caricamento`);
      return false;
    }

    try {
      // IMPORTANTE: Converti sempre matchId a stringa per coerenza
      const matchKey = String(matchId);
      const preferences = JSON.parse(localStorage.getItem('match_inversione_lati') || '{}');
      const isInverted = !!preferences[matchKey];
      console.log(`[useInversioneLati] Caricato stato per match ${matchKey}: ${isInverted}`);
      return isInverted;
    } catch (e) {
      console.error('Errore nel recupero preferenze inversione:', e);
      return false;
    }
  }, [matchId]);

  // Carica le preferenze all'inizio
  useEffect(() => {
    const isInverted = loadInversionePreferences();
    setInvertiLati(isInverted);
  }, [loadInversionePreferences]);

  // Reagisci agli eventi di storage (per sincronizzare tra tab/finestre)
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'match_inversione_lati') {
        const isInverted = loadInversionePreferences();
        setInvertiLati(isInverted);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [loadInversionePreferences]);

  // Funzione per invertire i lati
  const toggleInvertiLati = () => {
    const nuovoValore = !invertiLati;
    setInvertiLati(nuovoValore);

    if (matchId) {
      try {
        // IMPORTANTE: Usa sempre String(matchId) come chiave
        const matchKey = String(matchId);
        const preferences = JSON.parse(localStorage.getItem('match_inversione_lati') || '{}');
        preferences[matchKey] = nuovoValore;
        localStorage.setItem('match_inversione_lati', JSON.stringify(preferences));
        console.log(`[useInversioneLati] Aggiornato stato per match ${matchKey}: ${nuovoValore}`);

        // Dispara un evento custom per notificare altri componenti
        window.dispatchEvent(new CustomEvent('inversioneLatiChanged', {
          detail: { matchId: matchKey, inverted: nuovoValore }
        }));
      } catch (e) {
        console.error('Errore nel salvataggio preferenze inversione:', e);
      }
    }
  };

  // Funzione utility per ottenere il lato corretto
  const getLato = (teamA, teamB, scoreA = 0, scoreB = 0, penaltiesA = null, penaltiesB = null) => {
    return {
      visualTeamA: invertiLati ? teamB : teamA,
      visualTeamB: invertiLati ? teamA : teamB,
      visualScoreA: invertiLati ? scoreB : scoreA,
      visualScoreB: invertiLati ? scoreA : scoreB,
      visualPenaltiesA: invertiLati ? penaltiesB : penaltiesA,
      visualPenaltiesB: invertiLati ? penaltiesA : penaltiesB
    };
  };

  // Funzione per invertire i nomi dei tiratori negli assalti
  const getAssaltoInvertito = (assalto) => {
    if (!assalto) return assalto;

    if (invertiLati) {
      // Se i lati sono invertiti, scambia i nomi dei tiratori
      return {
        ...assalto,
        fencerA: assalto.fencerB || assalto.B,
        fencerB: assalto.fencerA || assalto.A,
        A: assalto.B,
        B: assalto.A
      };
    }

    // Altrimenti restituisci l'assalto originale
    return assalto;
  };

  return { invertiLati, toggleInvertiLati, getLato, getAssaltoInvertito };
}
